import { ReactNode } from 'react';
import { css } from '@linaria/atomic';
import { theme } from '@frontend/theme';

/**
 * Splits a text string around case-insensitive matches of each word in `term`
 * and wraps matches in a highlighted `<mark>`. Uses longest-match-first alternation
 * so that longer tokens always win over their substrings (e.g. "dog" matches before "og").
 * Returns the original text unchanged if `term` is empty or text is empty.
 */
export const highlightSegment = (text: string, term: string): ReactNode[] => {
  // Return early for empty/whitespace-only term or empty text
  if (!term.trim() || !text) return [text];

  // Deduplicate words, sort longest-first so longer tokens win in alternation
  const words = [...new Set(term.trim().split(/\s+/))].sort((a, b) => b.length - a.length);

  // Escape regex special characters to prevent injection (e.g. "a+b" → "a\\+b")
  const escaped = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

  // Build a capturing-group regex: /(Word1|Word2|...)/gi
  // Longest-first ordering ensures longer words are tried before their substrings
  // Using a capturing group with split retains the matched delimiters in the result
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');

  // Split text by matches — captured groups are retained as array elements
  // e.g. "hello world".split(/(hello)/) → ["", "hello", " world"]
  return text.split(regex).map((segment, i) =>
    regex.test(segment) ? (
      <mark key={i} className={highlightStyle}>
        {segment}
      </mark>
    ) : (
      segment
    )
  );
};

const highlightStyle = css`
  background-color: ${theme.colorsV2.yellow10()};
  border-radius: ${theme.spacing(0.25)};
  color: inherit;
`;
